/**
 * BenchRosterTable — the org-wide employees as a DataTable (demo /manage-team members table, /tables/data-tables).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Button, DataTable, type DataTableColumn } from '../../../components/ui/index.js';
import { Avatar, type EmployeeView } from '../shared';
import { AssignmentsCell, AttentionCell, CurrentFocusCell, LifecycleBadge, LoadCell, SkillChips } from './cells.js';

interface BenchRosterTableProps {
  employees: EmployeeView[];
  onOpen: (id: string) => void;
  loading?: boolean;
  empty?: React.ReactNode;
}

interface RowAction {
  label: string;
  primary: boolean;
}

/** Context action for a row. Attention takes precedence, then lifecycle, then load. */
function rowAction(emp: EmployeeView): RowAction {
  if (emp.lifecycle === 'terminated') return { label: 'View record', primary: false };
  if (emp.attention.level === 'blocked') return { label: 'Resolve', primary: true };
  if (emp.attention.level === 'needs_review') return { label: 'Review', primary: false };
  if (emp.lifecycle === 'needs_setup' || emp.attention.level === 'permission_needed')
    return { label: 'Finish setup', primary: false };
  if (emp.lifecycle === 'paused') return { label: 'Resume', primary: false };
  if (emp.load.state === 'unassigned') return { label: 'Assign', primary: true };
  return { label: 'Open', primary: false };
}

export const BenchRosterTable: React.FC<BenchRosterTableProps> = ({ employees, onOpen, loading, empty }) => {
  const columns: DataTableColumn<EmployeeView>[] = [
    {
      key: 'employee',
      header: 'Employee and skills',
      sortable: true,
      sortValue: (emp) => emp.name.toLowerCase(),
      render: (emp) => (
        <div className="flex items-center gap-2.5">
          <Avatar
            label={emp.name}
            kind={emp.kind}
            initials={emp.initials}
            size={32}
            lifecycle={emp.lifecycle}
            showKind
          />
          <div className="min-w-0">
            <Link
              to={`/workforce/${emp.id}`}
              onClick={(e) => e.stopPropagation()}
              className="block font-medium leading-tight text-heading hover:underline"
            >
              {emp.name}
            </Link>
            <div className="text-xs text-muted">
              {emp.role}
              {emp.tier ? ` · ${emp.tier}` : ''}
            </div>
            <SkillChips skills={emp.skills} />
          </div>
        </div>
      ),
    },
    { key: 'lifecycle', header: 'Lifecycle', render: (emp) => <LifecycleBadge lifecycle={emp.lifecycle} /> },
    {
      key: 'load',
      header: 'Assignment load',
      render: (emp) => <LoadCell load={emp.load} lifecycle={emp.lifecycle} />,
    },
    { key: 'attention', header: 'Attention', render: (emp) => <AttentionCell attention={emp.attention} /> },
    {
      key: 'assignments',
      header: 'Assignments',
      render: (emp) => <AssignmentsCell assignments={emp.assignments} terminated={emp.lifecycle === 'terminated'} />,
    },
    { key: 'focus', header: 'Current focus', render: (emp) => <CurrentFocusCell focus={emp.currentFocus} /> },
    {
      key: 'action',
      header: '',
      align: 'right',
      render: (emp) => {
        const action = rowAction(emp);
        return (
          <Button
            size="sm"
            variant={action.primary ? 'primary' : 'secondary'}
            aria-label={`${action.label} ${emp.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(emp.id);
            }}
          >
            {action.label}
          </Button>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={employees}
      rowKey={(emp) => emp.id}
      onRowClick={(emp) => onOpen(emp.id)}
      getRowAttention={(emp) => (emp.attention.level !== 'clear' ? 'danger' : undefined)}
      loading={loading}
      empty={empty}
      aria-label="Employees"
    />
  );
};
